import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import {
  TextCleanUpService
} from "@services/file-translations/file-translation-validation/text-clean-up/text-clean-up.service";

@Injectable({
  providedIn: 'root'
})
export class EmlTextExtractorService {

  constructor(
    private textCleanUpService: TextCleanUpService,
  ) { }

  /**
   * Extract text content from an EML file
   * @param file - The EML file to process
   * @returns Observable with the extracted text content
   */
  extractText(file: File): Observable<string> {
    return from(
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (!e.target?.result) {
            reject(new Error('Failed to read EML file'));
            return;
          }

          try {
            const emlContent = e.target.result as string;
            const textContent = this.parseEmlText(emlContent);

            // Remove email headers, including names and addresses
            const cleanedContent = this.removeHeaderInfo(textContent);
            const  cleanedTextForLanguageDetection = this.textCleanUpService.createSample(cleanedContent);
            resolve(cleanedTextForLanguageDetection);
          } catch (error) {
            reject(new Error(`Error extracting text from EML: ${error}`));
          }
        };
        reader.onerror = () => reject(new Error('Error reading EML file'));
        reader.readAsText(file);
      })
    )
  }

  /**
   * Parse the EML content to extract just the text part
   */
  private parseEmlText(emlContent: string): string {
    // First try to find a text/plain content type section
    const plainTextMatch = this.findPlainTextContent(emlContent);
    if (plainTextMatch) {
      return this.decodeContent(plainTextMatch);
    }

    // If no text/plain found, try to extract from text part of multipart/alternative
    const multipartMatch = this.findMultipartTextContent(emlContent);
    if (multipartMatch) {
      return this.decodeContent(multipartMatch);
    }

    const htmlContent = this.findHtmlContent(emlContent);
    if (htmlContent) {
      return htmlContent;
    }

    return this.extractTextFallback(emlContent);
  }

  private findHtmlContent(emlContent: string): string | null {
    const htmlRegex = /Content-Type:\s*text\/html(?:;\s*charset=[\w-"]+)?[\s\S]*?\r?\n\r?\n([\s\S]*?)(?:\r?\n\r?\n--(?:[-\w]+)|\r?\n\r?\nContent-Type|$)/i;
    const match = emlContent.match(htmlRegex);

    if (match && match[1]) {
      return this.extractTextFromHtml(match[1].trim());
    }

    return null;
  }

  /**
   * Look for a text/plain content section in the email
   */
  private findPlainTextContent(emlContent: string): string | null {
    const textPlainRegex = /Content-Type:\s*text\/plain(?:;\s*charset=[\w-]+)?(?:;\s*format=[\w-]+)?[\s\S]*?Content-Transfer-Encoding:\s*([^\r\n]+)[\s\S]*?\r?\n\r?\n([\s\S]*?)(?:\r?\n\r?\n--[-\w]+|\r?\n\r?\nContent-Type|$)/i;
    const match = emlContent.match(textPlainRegex);

    if (match && match[2]) {
      const encoding = match[1].trim();
      return this.decodeContent(match[2].trim(), encoding);
    }

    const simpleRegex = /Content-Type:\s*text\/plain(?:;\s*charset=[\w-]+)?(?:;\s*format=[\w-]+)?[\s\S]*?\r?\n\r?\n([\s\S]*?)(?:\r?\n\r?\n--[-\w]+|\r?\n\r?\nContent-Type|$)/i;
    const simpleMatch = emlContent.match(simpleRegex);

    if (simpleMatch && simpleMatch[1]) {
      return this.decodeContent(simpleMatch[1].trim());
    }

    return null;
  }

  /**
   * Find the text part in a multipart/alternative content
   */
  private findMultipartTextContent(emlContent: string): string | null {
    const boundaryMatch = emlContent.match(/Content-Type:\s*multipart\/(?:alternative|mixed|related)[\s\S]*?boundary="?([^";\s]+)"?/i);

    if (boundaryMatch && boundaryMatch[1]) {
      const boundary = boundaryMatch[1];
      const boundaryRegex = new RegExp(`--${boundary}[\\s\\S]*?Content-Type:\\s*text\\/plain[\\s\\S]*?\\r?\\n\\r?\\n([\\s\\S]*?)(?:\\r?\\n\\r?\\n--${boundary}|$)`, 'i');
      const match = emlContent.match(boundaryRegex);

      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return null;
  }

  private extractTextFallback(emlContent: string): string {
    // Skip headers (find first empty line)
    const headerEnd = emlContent.search(/\r?\n\r?\n/);
    if (headerEnd === -1) {
      return emlContent; // No headers found, return as is
    }

    let content = emlContent.substring(headerEnd + 2);

    // For HTML emails, we need to extract text before removing tags
    if (emlContent.includes('Content-Type: text/html') || /<html/i.test(content)) {
      content = this.extractTextFromHtml(content);
    } else {
      // For non-HTML emails continue with normal processing
      // Remove HTML content if present
      content = content.replace(/<html[\s\S]*?<\/html>/gi, '');
      content = content.replace(/<[^>]*>/g, '');

      // Remove boundary markers and content headers
      content = content.replace(/--(?:[-\w]+)(?:--)?\r?\n/g, '');
      content = content.replace(/Content-[^\r\n]*\r?\n/g, '');
    }

    return this.decodeContent(content.trim());
  }

  private extractTextFromHtml(htmlContent: string): string {
    let textContent = '';

    try {
      // Create a temporary DOM element to parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Get text content
      textContent = doc.body ? doc.body.textContent || '' : '';

      // Clean up the extracted text
      textContent = textContent.replace(/\s+/g, ' ').trim();
    } catch (e) {
      console.error('Failed to parse HTML content:', e);
      // Fallback to regex-based HTML tag removal
      textContent = htmlContent.replace(/<[^>]*>/g, ' ');
    }

    return textContent;
  }
  /**
   * Remove email headers (From, To, CC, etc.) that might include names
   * which can confuse language detection
   */
  private removeHeaderInfo(content: string): string {
    // If content is already empty, return early
    if (!content || content.trim() === '') {
      return content;
    }

    let cleanedContent = content;
    // Only remove email headers if they appear at the beginning of lines
    cleanedContent = cleanedContent.replace(/^From:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^To:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^CC:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^Cc:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^BCC:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^Bcc:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^Subject:.*$/mi, '');
    cleanedContent = cleanedContent.replace(/^Date:.*$/mi, '');

    // Only remove lines that are ONLY email addresses
    cleanedContent = cleanedContent.replace(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/mg, '');

    // If cleaning removed all content, return the original
    if (cleanedContent.trim() === '' && content.trim() !== '') {
      console.warn('Cleaning removed all content, returning original');
      return content;
    }

    return cleanedContent.trim();
  }

  /**
   * Decode email content from various encodings
   */
  /**
   * Decode email content from various encodings
   */
  private decodeContent(content: string, encoding?: string): string {
    // If we know it's base64 encoded
    if (encoding && encoding.toLowerCase() === 'base64') {
      try {
        return atob(content.replace(/\s+/g, ''));
      } catch (e) {
        console.error('Failed to decode base64 content:', e);
        // Fall through to other decoding methods
      }
    }

    // Handle quoted-printable encoding
    let decoded = content.replace(/=([0-9A-F]{2})/gi, (_, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });

    // Replace soft line breaks
    decoded = decoded.replace(/=\r?\n/g, '');

    // Handle special characters
    decoded = decoded.replace(/=3D/gi, '=');
    decoded = decoded.replace(/=20/gi, ' ');

    return decoded;
  }
}
